import { useState, useEffect, useContext, useMemo } from "react";
import { toast } from "react-toastify";

import { createCampaign, updateCampaign } from "../../services/campaigns.api";
import { listContactLists } from "../../services/contactLists.api";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";

const EMPTY = {
  name: "",
  whatsappId: "",
  contactListId: "",
  scheduledAt: "",
  templateId: "",
  templateVariables: null,
};

// datetime-local ("YYYY-MM-DDTHH:mm") → formato del motor ("YYYY-MM-DD HH:mm:ss").
const toBackendDate = (v) => (v ? `${v.replace("T", " ")}:00`.slice(0, 19) : null);
const toInputDate = (v) => (v ? String(v).replace(" ", "T").slice(0, 16) : "");

// Lógica del modal de Campaña Meta (0372 MC1). Reutiliza el motor de campañas (H10):
// crea/actualiza una Campaign con templateId + templateVariables (sin message1-5/media).
export function useMetaCampaignModal({ open, campaign, onSaved, onClose }) {
  const { whatsApps } = useContext(WhatsAppsContext);
  const wabaConnections = useMemo(
    () => (whatsApps || []).filter((w) => w.provider === "waba"),
    [whatsApps]
  );

  const [form, setForm] = useState(EMPTY);
  const [contactLists, setContactLists] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (campaign) {
      setForm({
        name: campaign.name || "",
        whatsappId: campaign.whatsappId || "",
        contactListId: campaign.contactListId || "",
        scheduledAt: toInputDate(campaign.scheduledAt),
        templateId: campaign.templateId || "",
        templateVariables: campaign.templateVariables || null,
      });
    } else {
      const only = wabaConnections.length === 1 ? wabaConnections[0].id : "";
      setForm({ ...EMPTY, whatsappId: only });
    }
    listContactLists({ pageNumber: 1, pageSize: 100 })
      .then(({ data }) =>
        setContactLists(Array.isArray(data?.records) ? data.records : Array.isArray(data) ? data : [])
      )
      .catch(toastError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, campaign]);

  const set = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const error = useMemo(() => {
    if (!form.name.trim()) return "El nombre es obligatorio.";
    if (!form.whatsappId) return "Selecciona la conexión WABA.";
    if (!form.templateId) return "Selecciona una plantilla aprobada.";
    if (!form.contactListId) return "Selecciona una lista de contactos.";
    if (!form.scheduledAt) return "Programa la fecha y hora de envío.";
    return null;
  }, [form]);

  const handleSubmit = async () => {
    if (error) {
      toast.info(error);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        whatsappId: Number(form.whatsappId),
        contactListId: Number(form.contactListId),
        templateId: Number(form.templateId),
        templateVariables: form.templateVariables ?? null,
        scheduledAt: toBackendDate(form.scheduledAt),
        // Intervalos anti-spam (defaults del motor H10).
        messageInterval: 45,
        longerIntervalAfter: 20,
        greaterInterval: 60,
      };
      if (campaign) await updateCampaign(campaign.id, payload);
      else await createCampaign(payload);
      toast.success(campaign ? "Campaña Meta actualizada." : "Campaña Meta creada.");
      if (onSaved) onSaved();
      if (onClose) onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return { wabaConnections, contactLists, form, set, error, saving, handleSubmit };
}
